// CustomerDashboard_With_EntityId.js
// نسخة محدثة تستخدم entityId من URL Login

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'universal-cookie';
import BookingForm from '../components/BookingForm';

 const MediaCarousel = ({ media }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  
  // Parse media URLs from comma-separated string
  const mediaUrls = media ? media.split(',').map(url => url.trim()).filter(url => url) : [];
  
  // Function to detect if URL is a video
  const isVideo = (url) => {
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov'];
    return videoExtensions.some(ext => url.toLowerCase().includes(ext));
  };
  
  // Auto-play functionality (only for images, not videos)
  useEffect(() => {
    if (!isAutoPlaying || mediaUrls.length <= 1) return;
    
    // Don't auto-advance if current item is a video
    if (isVideo(mediaUrls[currentIndex])) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        const nextIndex = prevIndex === mediaUrls.length - 1 ? 0 : prevIndex + 1;
        // Skip auto-play if next item is video
        if (isVideo(mediaUrls[nextIndex])) {
          setIsAutoPlaying(false);
          return prevIndex;
        }
        return nextIndex;
      });
    }, 3000);
    
    return () => clearInterval(interval);
  }, [isAutoPlaying, mediaUrls.length, currentIndex]);
  
  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? mediaUrls.length - 1 : prevIndex - 1
    );
    setIsAutoPlaying(false);
  };
  
  const goToNext = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === mediaUrls.length - 1 ? 0 : prevIndex + 1
    );
    setIsAutoPlaying(false);
  };
  
  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };
  
  if (mediaUrls.length === 0) return null;
  
  const currentMediaUrl = mediaUrls[currentIndex];
  const isCurrentVideo = isVideo(currentMediaUrl);
  
  return (
    <div className="carousel-container mb-6">
      <div className="carousel-wrapper">
        {/* Main Media Display */}
        <div className="carousel-image-container">
          {isCurrentVideo ? (
            <video 
              key={currentMediaUrl}
              src={currentMediaUrl}
              className="carousel-media"
              controls
              autoPlay
              muted
              playsInline
            >
              متصفحك لا يدعم عرض الفيديو
            </video>
          ) : (
            <img 
              src={currentMediaUrl} 
              alt={`صورة ${currentIndex + 1}`}
              className="carousel-media"
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/800x400?text=صورة+غير+متوفرة';
              }}
            />
          )}
          
          {/* Navigation Arrows */}
          {mediaUrls.length > 1 && (
            <>
              <button 
                onClick={goToPrevious}
                className="carousel-arrow carousel-arrow-left"
                aria-label="السابق"
              >
                ❮
              </button>
              <button 
                onClick={goToNext}
                className="carousel-arrow carousel-arrow-right"
                aria-label="التالي"
              >
                ❯
              </button>
            </>
          )}
          
          {/* Media Counter */}
          <div className="carousel-counter">
            {isCurrentVideo ? '📹' : '📷'} {currentIndex + 1} / {mediaUrls.length}
          </div>
        </div>
        
        {/* Dots Indicator */}
        {mediaUrls.length > 1 && (
          <div className="carousel-dots">
            {mediaUrls.map((url, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`carousel-dot ${index === currentIndex ? 'active' : ''} ${isVideo(url) ? 'video-dot' : ''}`}
                aria-label={`الذهاب إلى ${isVideo(url) ? 'الفيديو' : 'الصورة'} ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const cookies = new Cookies();
  
  const [customer, setCustomer] = useState(null);
  const [entity, setEntity] = useState(null); // ⭐ NEW: حالة لبيانات الجهة
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // NEW: حالة لإظهار نموذج الحجز
  const [showBookingForm, setShowBookingForm] = useState(false);

  // لإزالة الوقت من التاريخ
  const formatDate = (dateString) => {
    return dateString.split("T")[0];
  };

  // لإظهار الساعة والدقائق فقط
  const formatTime = (timeString) => {
    if (!timeString) return "";
    return timeString.slice(0, 5); // يأخذ hh:mm فقط
  };

  // لو لديك مدة مثل "18:00:00 - 01:00:00"
  const formatTimeRange = (range) => {
    if (!range) return "";
    const [from, to] = range.split(" - ");
    return `${formatTime(from)} - ${to ? formatTime(to) : ""}`;
  };


  // ⭐ NEW: جلب entityId من localStorage
  const getEntityId = () => {
    const storedEntityId = localStorage.getItem('customerEntityId');
    const cookieEntityId = cookies.get('points1smsssprivrw');
    
    const entityId = storedEntityId || cookieEntityId;
    
    console.log('🔍 Getting Entity ID:', {
      fromLocalStorage: storedEntityId,
      fromCookie: cookieEntityId,
      final: entityId
    });
    
    return entityId;
  };

  // التحقق من الـ Authentication
  const checkAuth = () => {
    const token = localStorage.getItem('CustomerTokenA');
    const entityId = getEntityId();
    
    if (!token) {
      console.warn('⚠️ No token found, redirecting to login');
      navigate('/Customer/login');
      return false;
    }
    
    if (!entityId) {
      console.warn('⚠️ No entity ID found, redirecting to login');
      alert('الرجاء تسجيل الدخول من خلال رابط الجهة الصحيح');
      navigate('/Customer/login');
      return false;
    }
    
    return true;
  };

  useEffect(() => {
    if (checkAuth()) {
      const entityId = getEntityId();
      fetchCustomerData();
      fetchEntityData(entityId); // ⭐ NEW: جلب بيانات الجهة
      fetchAppointments(entityId); // ⭐ MODIFIED: تمرير entityId
    }
  }, []);

  // دالة لجلب بيانات العميل
  const fetchCustomerData = async () => {
    try {
      const token = localStorage.getItem('CustomerTokenA');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/customer/profile`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.status === 401) {
        localStorage.removeItem('CustomerTokenA');
        localStorage.removeItem('CustomerData');
        localStorage.removeItem('userType');
        localStorage.removeItem('customerEntityId');
        navigate('/Customer/login');
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setCustomer(data);
        console.log('✅ Customer data loaded:', data);
      }
    } catch (error) {
      console.error('❌ Error fetching customer data:', error);
    }
  };

  // ⭐ NEW: دالة لجلب بيانات الجهة
  const fetchEntityData = async (entityId) => {
    try {
      const token = localStorage.getItem('CustomerTokenA');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/Customer/myEntityGet?MyEntity_id=${entityId}`,  //  
      
        {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      }
      );
      
      if (response.ok) {
        const data = await response.json();
        setEntity(data);
        console.log('✅ Entity data loaded:', data);
      } else {
        console.error('❌ Failed to fetch entity data:', response.status);
      }
    } catch (error) {
      console.error('❌ Error fetching entity data:', error);
    }
  };

  // ⭐ MODIFIED: دالة لجلب المواعيد مع فلترة حسب entityId
  const fetchAppointments = async (entityId) => {
    try {
      const token = localStorage.getItem('CustomerTokenA');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/Customer/appointments?entity_id=${entityId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.status === 401) {
        localStorage.removeItem('CustomerTokenA');
        localStorage.removeItem('CustomerData');
        localStorage.removeItem('customerEntityId');
        navigate('/Customer/login');
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
         console.log(  'fetchedAppointments:', data );
        // ⭐ تصفية الحجوزات لتظهر فقط حجوزات هذه الجهة
        const filteredAppointments = data.filter(apt => 
          apt.entity_id == entityId || apt.Entity_id == entityId
        );

        console.log(  'filteredAppointments:', filteredAppointments ); 
     
       if (filteredAppointments.length > 0)
        {
         console.log(" filteredAppointments[0].accept_cancellation", filteredAppointments[0].accept_cancellation);
       }
        setAppointments(filteredAppointments);
        console.log('✅ Appointments loaded:', filteredAppointments.length, 'for entity', entityId);
      }
    } catch (error) {
      console.error('❌ Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  // دالة لبدء الحجز
  const handleStartBooking = () => {
    const entityId = getEntityId();
    if (!entityId) {
      alert('معرف الجهة غير موجود');
      return;
    }
    setShowBookingForm(true);
  };

  // دالة عند نجاح الحجز
  const handleBookingSuccess = (bookingData) => {
    console.log('✅ Booking successful:', bookingData);
    
    setShowBookingForm(false);
    
   // window.location.href = bookingData.payment_link;

    // تحديث قائمة المواعيد
    const entityId = getEntityId();
    fetchAppointments(entityId);
    //alert(`تم الحجز بنجاح!\nالجزء: ${bookingData.reserved_part}\nالوقت: ${bookingData.appointment_time} - ${bookingData.end_time}\nالتكلفة: ${bookingData.amount} جنيه`);
  
    console.log(`تم الحجز بنجاح!\nالجزء: ${bookingData.reserved_part}\nالوقت: ${bookingData.appointment_time} - ${bookingData.end_time}\nالتكلفة: ${bookingData.amount} جنيه`);
  };

  // ⭐ MODIFIED: دالة إلغاء الحجز مع فحص السياسة
  const handleCancelAppointment = async (appointmentId, acceptCancellation) => {
    if (acceptCancellation === 'no') {
      alert('عذراً، هذه الجهة لا تسمح بإلغاء الحجوزات');
      return;
    }
    
    if (!window.confirm('هل أنت متأكد من إلغاء هذا الحجز؟')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('CustomerTokenA');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/appointment/${appointmentId}/cancel`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      const data = await response.json();
      
      if (response.ok) {
        alert('تم إلغاء الحجز بنجاح');
        const entityId = getEntityId();
        fetchAppointments(entityId);
      } else {
        alert(data.message || 'حدث خطأ في إلغاء الحجز');
      }
    } catch (error) {
      console.error('❌ Error cancelling appointment:', error);
      alert('حدث خطأ في الاتصال بالخادم');
    }
  };

  // دالة تسجيل الخروج
  const handleLogout = () => {
    const storedEntityId = localStorage.getItem('customerEntityId');
    localStorage.removeItem('CustomerTokenA');
    localStorage.removeItem('CustomerData');
    localStorage.removeItem('userType');
    localStorage.removeItem('customerEntityId'); // ⭐ NEW: حذف entityId
    cookies.remove('points1smsssprivrw'); // حذف من الكوكيز أيضاً
    navigate('/'+storedEntityId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-blue-600 to-teal-500 flex-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-blue-600 to-teal-500">
      {/* Header */}
      <div className="glass p-4 m-4">
        <div className="flex-between">
          <div>
            <h1 className="text-white text-2xl font-bold">لوحة تحكم العميل</h1>
            {/* ⭐ NEW: عرض اسم الجهة */}
            {entity && (
              <p className="text-white opacity-80 text-sm mt-1">
                🏢 {entity.name} - {entity.specialization}
              </p>
            )}
          </div>
          <button onClick={handleLogout} className="btn btn-secondary">
            تسجيل خروج
          </button>
        </div>
      </div>

      {/* Modal للحجز الجديد */}
      {showBookingForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex-center z-50">
          <div className="glass p-6 m-4 max-w-2xl w-full max-h-screen overflow-y-auto">
            <BookingForm 
              entityId={getEntityId()} // ⭐ تمرير entityId
              onSuccess={handleBookingSuccess}
              onCancel={() => setShowBookingForm(false)}
            />
          </div>
        </div>
      )}

      <div className="container mx-auto p-4">
        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`btn ${activeTab === 'dashboard' ? 'btn-primary' : 'btn-secondary'}`}
          >
            الرئيسية
          </button>
          <button
            onClick={() => setActiveTab('appointments')}
            className={`btn ${activeTab === 'appointments' ? 'btn-primary' : 'btn-secondary'}`}
          >
            حجوزاتي
          </button>
          <button
            onClick={() => setActiveTab('book')}
            className={`btn ${activeTab === 'book' ? 'btn-primary' : 'btn-secondary'}`}
          >
            حجز جديد
          </button>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="glass p-6">
            {/* carosel should be here */}
            {/* Photo Carousel */}
            {entity?.has_media === 'yes' && entity?.media && (
              <MediaCarousel  media={entity.media} />
            )}
            <h2 className="text-white text-xl font-bold mb-4">
              مرحباً {customer?.name || 'عزيزي العميل'}
            </h2>
            
            {/* ⭐ NEW: معلومات العميل */}
            <div className="glass-dark p-4 rounded-lg mb-4">
              <h3 className="text-white font-bold mb-2">معلوماتي</h3>
              <div className="text-white">
                <p className="mb-2">📱 رقم الهاتف: {customer?.phone}</p>
                <p className="mb-2">📧 البريد الإلكتروني: {customer?.email}</p>
              </div>
            </div>

            {/* ⭐ NEW: معلومات الجهة */}
            {entity && (
              <div className="glass-dark p-4 rounded-lg">
                <h3 className="text-white font-bold mb-2">معلومات هامة </h3>
                <div className="text-white">
                  <p className="mb-2">🏢 الاسم: {entity.name}</p>
                  <p className="mb-2">📋 التليفون المخصص للحجز: {entity.specialization}</p>
                  {/* <p className="mb-2">💰 رسوم الحجز: {entity.reservation_fee} جنيه</p>
                  <p className="mb-2">⏱️ وحدة الحجز: {entity.reservation_unit} دقيقة</p> */}
                  {entity.phone && (
                    <p className="mb-2">📞 الهاتف: {entity.phone}</p>
                  )}

                  {entity.notes_to_customer && entity.notes_to_customer.trim().length > 0 && (
                    <p className="mb-2">📝 ملاحظات للعميل: {entity.notes_to_customer}</p>
                  )}
                  
                </div>
              </div>
            )}
          </div>
        )}

        {/* Appointments Tab */}
        {activeTab === 'appointments' && (
          <div className="glass p-6">
            <h2 className="text-white text-xl font-bold mb-4">
              حجوزاتي في {entity?.name || 'الجهة'}
            </h2>
            
            {appointments.length === 0 ? (
              <p className="text-white opacity-70">لا توجد حجوزات</p>
            ) : (
              <div className="flex-column gap-4">
                {appointments.map((appointment) => (
                  <div key={appointment.id} className="glass-dark p-4 rounded-lg">
                    <div className="flex-between mb-3">
                      <div>
                        <h3 className="text-white font-bold text-lg">
                          {appointment.reserved_part}
                        </h3>
                        <p className="text-white opacity-80 text-sm">
                          📅 {appointment.appointment_date}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm ${
                        appointment.status === 'confirmed' ? 'bg-green-500' :
                        appointment.status === 'pending' ? 'bg-yellow-500' :
                        appointment.status === 'cancelled' ? 'bg-red-500' :
                        'bg-gray-500'
                      }`}>
                        {appointment.status === 'confirmed' ? 'مؤكد' :
                         appointment.status === 'pending' ? 'قيد الانتظار' :
                         appointment.status === 'cancelled' ? 'ملغي' :
                         appointment.status === 'completed' ? 'مكتمل' : appointment.status}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div className="text-white text-sm">
                        <span className="opacity-70">🕐 الوقت:</span>
                        <span className="ml-2">
                          {appointment.appointment_time} - {appointment.end_time}
                        </span>
                      </div>
                      <div className="text-white text-sm">
                        <span className="opacity-70">⏱️ المدة:</span>
                        <span className="ml-2">{appointment.reservation_duration} دقيقة</span>
                      </div>
                      <div className="text-white text-sm">
                        <span className="opacity-70">💰 التكلفة:</span>
                        <span className="ml-2">{appointment.amount} جنيه</span>
                      </div>
                      <div className="text-white text-sm">
                        <span className="opacity-70">الخدمات المحجوزة  :</span>
                        <span className="ml-2">{appointment.services}</span>
                      </div>
                      <div className="text-white text-sm">
                        <span className="opacity-70"> رقم الحجز  :</span>
                        <span className="ml-2">{appointment.id} </span>
                      </div>
                      <div className="text-white text-sm">
                        {/* <span className="opacity-70">💳 الدفع:</span>
                        <span className="ml-2">
                          {appointment.payment_status === 'completed' ? 'مدفوع ✅' : 'قيد الانتظار'}
                        </span> */}
                      </div>
                    </div>
                    
                    {appointment.notes && (
                      <p className="text-white opacity-70 text-sm mb-3">
                        📝 {appointment.notes}
                      </p>
                    )}
                    
                    {/* زر الإلغاء */}
                    {appointment.status !== 'cancelled' && 
                     appointment.status !== 'completed' && (
                      <>
                        {entity?.accept_cancellation === 'yes' ? (
                          <button
                            onClick={() => handleCancelAppointment(
                              appointment.id, 
                              entity.accept_cancellation
                            )}
                            className="btn btn-secondary w-full"
                          >
                            إلغاء الحجز
                          </button>
                        ) : (
                          <p className="text-yellow-300 text-sm text-center">
                            ⚠️ لا يمكن إلغاء هذا الحجز
                          </p>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Book Tab */}
        {activeTab === 'book' && (
          <div className="glass p-6">
            <h2 className="text-white text-xl font-bold mb-4">
              حجز موعد في {entity?.name || 'الجهة'}
            </h2>
            
            {entity && (
              <div className="glass-dark p-4 rounded-lg mb-4">
                <h3 className="text-white font-bold text-lg mb-2">
                  {entity.name}
                </h3>
                <p className="text-white opacity-80 text-sm mb-2">
                  {entity.specialization}
                </p>
                <p className="text-white text-sm mb-3">
                  💰 رسوم الحجز: {entity.reservation_fee} جنيه
                </p>
                <p className="text-white text-sm mb-4">
                  ⏱️ وحدة الحجز ( أقل مدة  ): {entity.reservation_unit} دقيقة
                </p>
                <button
                  onClick={handleStartBooking}
                  className="btn btn-primary w-full"
                >
                  احجز الآن
                </button>
              </div>
            )}
            
            {!entity && (
              <p className="text-white opacity-70">جاري تحميل بيانات الجهة...</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDashboard;